import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LatLngBounds, latLngBounds } from 'leaflet';
import { AddressSearchResult, BuildingFeature, PanelParameters } from '../types/types';
import '../utils/leaflet-setup';
import '../index.css';
import { getParcel } from '../services/api';
import MapLayout from '@syte-tech/ui/components/layout/MapLayout';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MapSidebarHeader from '../components/MapSidebarHeader';
import AddressSearchMap from '../components/AddressSearchMap';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import SvgHouse from '@syte-tech/ui/icons/House';
import Button from '@mui/material/Button';
import PanelParameterControls from '../components/PanelParameterControls';
import MapSidebar from '../components/MapSidebar';
import posthog from 'posthog-js';
import Alert from '@mui/material/Alert';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Suggestion = any;

const AddressSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [searchResult, setSearchResult] = useState<AddressSearchResult | null>(null);
  const [selectedBuilding, setSelectedBuilding] = useState<Suggestion | null>(null);
  const [mapBounds, setMapBounds] = useState<LatLngBounds | undefined>();
  const [parameters, setParameters] = useState<PanelParameters>({
    width: 1.722, // Default panel width in meters
    height: 1.134, // Default panel height in meters
    efficiency: 0.2, // Default panel efficiency
    min_tilt: 0 // Default minimum tilt in degrees
  });
  const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_API_KEY;

  posthog.init(POSTHOG_API_KEY, { 
      api_host: 'https://t.syte.ms/', 
      defaults: '2025-05-24', 
      autocapture: false, 
  })

  const debounceTimeout = useRef<NodeJS.Timeout | null>(null); // Ref to store the debounce timeout

  const fetchSuggestions = async (query: string) => {
    try {
      const suggestionCountLimit = 5;
      const response = await fetch(`https://photon.komoot.io/api/?q=${query}&limit=${suggestionCountLimit}&lang=de`);
      const data = await response.json();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setSuggestions(data.features.filter((s: any) => s.properties.type !== 'street') || []);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      posthog.capture('pvpro_error', {
        errorType: 'address_search',
        query: query,
      });
    }
  };

  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);

    // Clear the previous timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Set a new timeout to fetch suggestions
    debounceTimeout.current = setTimeout(() => {
      fetchSuggestions(query);
    }, 300); // 300ms debounce delay
  };

  const handleSuggestionSelect = async (suggestion: Suggestion) => {
    setSearchQuery(`${suggestion.properties.street} ${suggestion.properties.housenumber}, ${suggestion.properties.postcode} ${suggestion.properties.city}`);
    setSuggestions([]);
    const [lon, lat] = suggestion.geometry.coordinates;
    try {
      const result = await getParcel(lon, lat); // fetch parcel data from backend
      setSearchResult(result);

      // Calculate bounds from all building coordinates
      if (result.features.length > 0) {
        const coordinates = result.features.flatMap(feature =>
          feature.geometry.coordinates[0][0] // First polygon, first ring
        );
        const lats = coordinates.map(coord => coord[1]);
        const lngs = coordinates.map(coord => coord[0]);
        const bounds = latLngBounds(
          [Math.min(...lats), Math.min(...lngs)],
          [Math.max(...lats), Math.max(...lngs)]
        );
        setMapBounds(bounds); // center the map on the returned building
      }
    } catch (error) {
      console.error('Failed to fetch parcel data:', error);

      posthog.capture('pvpro_error', {
        errorType: 'parcel_fetch',
        address: [suggestion.properties.street, suggestion.properties.housenumber, suggestion.properties.postcode, suggestion.properties.city].filter(Boolean).join(' '),
      });
      
      alert('Failed to fetch parcel data. Please try again.');
    }
  };

  const handleBuildingSelect = useCallback((feature: BuildingFeature) => {
    setSelectedBuilding(feature);
  }, []);

  const handleCalculate = () => {
    if (selectedBuilding) {
      posthog.capture('pvpro_start_calculate', {
        building: selectedBuilding.properties.identifier,
        parameters: parameters,
      });

      navigate(`/results/${selectedBuilding.properties.identifier}`, {
        state: { parameters, building: selectedBuilding }
      });
    }
  };

  return (
    <MapLayout map={(
      <AddressSearchMap
        mapBounds={mapBounds}
        buildings={searchResult?.features}
        selectedBuilding={selectedBuilding}
        onBuildingClick={handleBuildingSelect}
      />
    )}>
      <MapSidebar>
        <MapSidebarHeader />
        {(() => {
            const now = new Date();
            const germanTime = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Berlin" }));
            const hours = germanTime.getHours();
            const day = germanTime.getDay(); // 0 = Sunday, 6 = Saturday
            if (hours < 8 || hours >= 21 || day === 0 || day === 6) {
            return (
              <Alert severity="warning">
              Außerhalb der Geschäftszeiten (8:00 bis 21:00 Uhr) sowie an Wochenenden ist die Nutzung des Tools eingeschränkt und Antworten können stark verzögert sein.
              </Alert>
            );
            }
          return null;
        })()}
        <Box padding={3}>
          <Autocomplete
            freeSolo
            options={suggestions || []}
            getOptionLabel={(suggestion) => (
              [suggestion.properties.street, suggestion.properties.housenumber, suggestion.properties.postcode, suggestion.properties.city].filter(Boolean).join(' ')
            )}
            getOptionKey={(suggestion) => suggestion.properties.osm_id}
            isOptionEqualToValue={(option, value) => option.properties.osm_id === value.properties.osm_id}
            filterOptions={options => options}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Adresse suchen"
              />
            )}
            inputValue={searchQuery || ''}
            onInputChange={(event, value) => {
              handleSearchQueryChange(value);
            }}
            onChange={(event, value) => {
              if (value) {
                handleSuggestionSelect(value);
              } else {
                setSearchQuery('');
                setSuggestions([]);
                setSearchResult(null);
                setSelectedBuilding(null);
              }
            }}
          />
        </Box>
        {searchResult && (
          <Box padding={3} height="calc(100vh - 550px)" overflow="auto">
            <Typography variant="body2" paddingBottom={3}>{selectedBuilding ? 'Gewähltes Gebäude': 'Gebäude auswählen'}</Typography>
            <List>
              {searchResult.features.map((buildingFeature, index) => (
                <ListItemButton
                  key={buildingFeature.properties.identifier}
                  onClick={() => handleBuildingSelect(buildingFeature)}
                  selected={buildingFeature.properties.identifier === selectedBuilding?.properties.identifier}
                >
                  <ListItemIcon>
                    <SvgHouse />
                  </ListItemIcon>
                  <ListItemText
                    primary={buildingFeature.properties.usage || buildingFeature.properties.label || `${buildingFeature.properties.address || 'Gebäude'} ${index}`}
                    secondary={[
                      buildingFeature.properties.floors && `${buildingFeature.properties.floors} ${buildingFeature.properties.floors > 1 ? 'Stockwerke' : 'Stockwerk'}`,
                      buildingFeature.properties.built_surface_area && `${buildingFeature.properties.built_surface_area.toFixed(1)} m²`,
                    ].filter(Boolean).join(', ')}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        )}
        {selectedBuilding && (
          <PanelParameterControls
            padding={3}
            parameters={parameters}
            onParametersChange={setParameters}
          />
        )}
        {selectedBuilding && (
          <Box padding={3}>
            <Button
              color="primary"
              onClick={handleCalculate}
              fullWidth
            >
              Modulauslegung berechnen
            </Button>
          </Box>
        )}
      </MapSidebar>
    </MapLayout>
  );
};

export default AddressSearchPage;